import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Lock, Users, CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { IMG } from "@/lib/brand";
import { Button } from "@/components/ui/button";

const MONTHS = [
  { label: "October 2026", m: 9 },
  { label: "November 2026", m: 10 },
  { label: "December 2026", m: 11 },
];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_STYLE = {
  available: "bg-emerald-500/20 border-emerald-400 text-emerald-300 hover:bg-emerald-500/40",
  full: "bg-red-500/20 border-red-400 text-red-300",
  closed: "bg-slate-600/20 border-slate-600 text-slate-500",
};

export default function ExpeditionMap() {
  const [days, setDays] = useState([]);
  const [live, setLive] = useState(false);
  useEffect(() => { api.get("/public/calendar").then((r) => setDays(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/public/settings").then((r) => setLive(!!r.data.registration_live)).catch(() => {}); }, []);

  const byDate = Object.fromEntries(days.map((d) => [d.date, d]));

  const renderMonth = ({ label, m }) => {
    const first = new Date(2026, m, 1);
    const startPad = (first.getDay() + 6) % 7; // Monday start
    const daysInMonth = new Date(2026, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return (
      <div key={label} className="glass border border-turquoise/20 rounded-3xl p-5" data-testid={`expedition-month-${m}`}>
        <h3 className="font-display font-bold text-xl text-gold mb-4">{label}</h3>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-turquoise/70 font-semibold mb-2">
          {DOW.map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const iso = `2026-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const info = byDate[iso];
            if (!info) return <div key={i} className="aspect-square flex items-center justify-center text-white/25 text-sm">{d}</div>;
            const cls = STATUS_STYLE[info.display] || STATUS_STYLE.closed;
            const content = (
              <div className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-sm font-bold transition-all ${cls}`}>
                <span>{d}</span>
                {info.display === "available" && <MapPin className="w-3 h-3" />}
                {info.display === "full" && <Users className="w-3 h-3" />}
                {info.display === "closed" && <Lock className="w-3 h-3" />}
              </div>
            );
            return (
              <motion.div key={i} whileHover={{ scale: info.display === "available" ? 1.12 : 1 }} data-testid={`expedition-day-${iso}`}>
                {info.display === "available" ? <Link to={live ? "/register" : "/join"} title={live ? `Book ${iso}` : "Join the mailing list"}>{content}</Link> : content}
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-navy min-h-screen text-white">
      <Navbar />
      <section className="relative pt-32 pb-12 grain">
        <div className="absolute inset-0 opacity-10"><img src={IMG.mapTexture} className="w-full h-full object-cover" alt="" /></div>
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <span className="text-turquoise font-bold tracking-[0.3em] text-sm">TERM 1</span>
          <h1 className="font-display font-black text-4xl sm:text-6xl mt-3">TERM 1 EXPEDITION MAP</h1>
          <p className="text-white/70 mt-4">Each highlighted marker is an operating Adventure Rock day · 3:30–6:00 PM. Tap an available day to begin registration.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-6 text-xs font-semibold">
            <Legend cls="bg-emerald-500/30 border-emerald-400" label="Available" />
            <Legend cls="bg-blue-500/30 border-blue-400" label="Booked" />
            <Legend cls="bg-red-500/30 border-red-400" label="Full" />
            <Legend cls="bg-slate-600/30 border-slate-600" label="Closed" />
            <Legend cls="bg-violet-500/30 border-violet-400" label="Credit Used" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {MONTHS.map(renderMonth)}
        </div>
        <div className="text-center mt-12">
          <Link to={live ? "/register" : "/join"}><Button className="bg-gold hover:bg-gold/90 text-navy font-bold rounded-full px-8" data-testid="expedition-register-btn">{live ? "Book Your Explorer's Adventure" : "Join the Mailing List"}</Button></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Legend({ cls, label }) {
  return <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded border ${cls}`} /> {label}</span>;
}
